/**
 * Mock Investigation Plan Data
 * Based on screenshots from Vercel prototype
 * Use this until backend API is ready
 */

import { InvestigationPlan, InvestigationStatus } from './types';

export const MOCK_INVESTIGATION_PLAN: InvestigationPlan = {
  id: 'inv-mock-001',
  caseId: 'case-cf2603c0',
  caseDisplayId: 'CF2603C0',
  status: InvestigationStatus.READY,
  generatedAt: new Date('2026-07-20T10:30:00Z'),
  checklist:
    '- [ ] Immediate Data Retrieval\n- [ ] Policy Audit\n- [ ] Witness Identification\n- [ ] Reporter Follow-up\n- [ ] Subject Interview',

  steps: [
    {
      id: 'step-1',
      order: 1,
      title: 'Immediate Data Retrieval',
      description:
        "Pull the last 90 days of overtime allocation reports for David Miller's facility.",
      status: 'pending',
    },
    {
      id: 'step-2',
      order: 2,
      title: 'Policy Audit',
      description:
        'Executing native query to Policy Management module. Cross-referencing Acme Corp. "Respect in the Workplace" and "Anti-Retaliation" policies. (Flagging relevant clauses 4.2 and 5.1).',
      hasSpecialAction: true, // "View Findings" link
      status: 'pending',
    },
    {
      id: 'step-3',
      order: 3,
      title: 'Witness Identification',
      description:
        "Request recent transfer logs from HR to identify any employees who moved out of David's department within the last 6 months. Schedule confidential interviews with identified individuals.",
      hasSpecialAction: true, // Sparkle emoji for scheduling
      status: 'pending',
    },
    {
      id: 'step-4',
      order: 4,
      title: 'Reporter Follow-up',
      description:
        'Send a secure, anonymous message to the reporter requesting specific dates of the production meetings mentioned.',
      status: 'pending',
    },
    {
      id: 'step-5',
      order: 5,
      title: 'Subject Interview',
      description:
        'Schedule a formal interview with David Miller (to be conducted only after initial fact-finding is complete).',
      status: 'pending',
    },
  ],

  policies: [
    {
      policyId: 'policy-67',
      title: 'Respect in the Workplace - Anti-Harassment Policy',
      clauses: ['4.2', '5.1'],
      relevance:
        'Directly relevant to allegations of aggressive, degrading language and retaliation threats.',
    },
    {
      policyId: 'policy-42',
      title: 'Anti-Retaliation Policy',
      clauses: ['3.2'],
      relevance:
        'Covers explicit threats to cut overtime hours for staff who raised safety concerns to HR.',
    },
  ],

  policyFindings: {
    excerpts: [
      {
        id: 'excerpt-1',
        title: 'Respect in the Workplace v2.1 (Clause 4.2)',
        excerpt:
          'Supervisors must not use threatening language, implied or explicit, regarding job security or project assignment to enforce compliance or discourage reporting.',
      },
      {
        id: 'excerpt-2',
        title: 'Anti-Retaliation Policy (Clause 5.1)',
        excerpt:
          'Any alteration of working hours, compensation, or role responsibilities following a protected disclosure will be subject to immediate review for retaliatory intent.',
      },
    ],
    analysis:
      'The allegation that David Miller explicitly threatened to pull team members off high-profile projects is a direct violation of Clause 4.2. Furthermore, if the threat to cut overtime hours is substantiated, it triggers automatic retaliation protocols under Clause 5.1. Establishing a strict timeline of any recent overtime or project reassignments is critical to determining liability.',
  },

  targetedQuestions: [
    {
      id: 'q-witnesses',
      stakeholderType: 'WITNESSES',
      stakeholderName: 'Former Team Members',
      questions: [
        'During your time at the facility, did you ever observe overtime shifts being used as a disciplinary tool?',
        "Can you describe David Miller's typical communication style during shift production meetings?",
      ],
    },
    {
      id: 'q-reporter',
      stakeholderType: 'ANONYMOUS_REPORTER',
      questions: [
        'Can you provide the names of any other supervisors who were present during the production meetings where these threats were made?',
        'Did you formally report the safety concerns to HR prior to this hotline submission, or was this the first instance?',
      ],
    },
    {
      id: 'q-subject',
      stakeholderType: 'SUBJECT',
      stakeholderName: 'David Miller',
      questions: [
        'Can you walk me through your standard process for addressing safety concerns raised by your shift workers?',
        'How do you determine the allocation of overtime hours among your team, and what factors might lead to a reduction in those hours?',
      ],
    },
  ],

  scheduling: {
    stepId: 'step-3',
    witnesses: [
      {
        name: 'Sarah Jenkins',
        role: 'Logistics',
        email: 'sarah.jenkins@example.com',
      },
      {
        name: 'Mark Tomin',
        role: 'Logistics',
        email: 'mark.tomin@example.com',
      },
    ],
    subject: 'Confidential HR Sync - Case CF2603C0',
    message:
      'Hi Sarah and Mark, I am scheduling a brief, confidential sync to discuss some standard workplace environment queries. Please select the most convenient time below.',
    suggestedTimes: [
      {
        day: 'Today',
        time: '3:30 PM',
        duration: '30 min',
        availability: 'high',
      },
      {
        day: 'Tomorrow',
        time: '10:00 AM',
        duration: '30 min',
        availability: 'high',
      },
      {
        day: 'Tomorrow',
        time: '1:15 PM',
        duration: '30 min',
        availability: 'high',
      },
    ],
    teamsLinkGenerated: true,
  },

  nextBestAction: {
    stepId: 'step-3',
    title: 'Schedule Witness Interviews',
    description:
      'Based on your approved plan, the priority is to secure confidential interviews with the former team members identified by HR. Secure calendar holds now to prevent spoliation or retaliation.',
    actionType: 'schedule',
  },
};

export const MOCK_ACCEPTED_PLAN: InvestigationPlan = {
  ...MOCK_INVESTIGATION_PLAN,
  status: InvestigationStatus.ACCEPTED,
  acceptedAt: new Date('2026-07-20T10:45:00Z'),
  steps: MOCK_INVESTIGATION_PLAN.steps.map((step) => ({
    ...step,
    status: 'pending' as const,
  })),
};

// Mock data for different states
export const getMockPlanByStatus = (
  status: InvestigationStatus,
): InvestigationPlan | null => {
  switch (status) {
    case InvestigationStatus.GENERATING:
      return {
        ...MOCK_INVESTIGATION_PLAN,
        status: InvestigationStatus.GENERATING,
        steps: [],
        targetedQuestions: [],
        policies: [],
      };

    case InvestigationStatus.READY:
      return MOCK_INVESTIGATION_PLAN;

    case InvestigationStatus.ACCEPTED:
      return MOCK_ACCEPTED_PLAN;

    case InvestigationStatus.FAILED:
      return {
        ...MOCK_INVESTIGATION_PLAN,
        status: InvestigationStatus.FAILED,
      };

    case InvestigationStatus.IDLE:
    default:
      return null;
  }
};
